import { Auth, CognitoUser } from "@aws-amplify/auth";
import { Hub } from "@aws-amplify/core";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import apiClient from "src/utils/apiClient";
import axios from "axios";
import { getAuthHeaders } from "src/utils/authUtils";
import { ENV } from "src/utils/detectEnv";

const USER = "user";
const TOKEN = "token";
const ASSETS_LAST_MODIFIED = "assets-last-modified";

const getCurrentUser = async (): Promise<CognitoUser | null> => {
  try {
    return await Auth.currentAuthenticatedUser();
  } catch (e) {
    // TODO: add error log
    console.error(e);
    return null;
  }
};

const signIn = () =>
  Auth.federatedSignIn({
    customProvider: "FederateOIDC",
    customState: window.location.href,
  });

// SAAM service identity used by the Hydra/Cypress integration test. Its alias
// follows the `svc-mw-<account>-midwaysaam` convention.
const TEST_USER_PREFIX = "svc-mw";

// LDAP groups granted to the integration-test user so the authenticated app
// shell renders (see routes.tsx / userSlice authorization). Includes an admin
// group so admin-gated surfaces are also reachable from the test.
const TEST_USER_LDAP_GROUPS = ["whs-safer-admin", "workingwell-engineer"];

// Whether the current stage is Beta or local/dev. The test-user LDAP override
// is allowed ONLY here, never on gamma or prod: Hydra tests only run on Beta,
// and this override must not take effect in the prod deployment where it would
// be an authorization bypass. Local/dev builds resolve "gamma" to "test",
// which the app treats as beta (see buildRegionUrl in regionUtils).
const isBetaOrLocalEnv = (stage: string): boolean => {
  const normalizedStage = stage.toLowerCase();
  return normalizedStage === ENV.BETA || normalizedStage === "test";
};

const login = async () => {
  const stage: string = "gamma";
  if (stage !== "test") {
    const lastModified = await getAssetsLastModified();
    if (localStorage.getItem(ASSETS_LAST_MODIFIED) === null) {
      localStorage.setItem(ASSETS_LAST_MODIFIED, lastModified);
    } else if (localStorage.getItem(ASSETS_LAST_MODIFIED) !== lastModified) {
      localStorage.removeItem(ASSETS_LAST_MODIFIED);
      // refreshPage();
    }
  }

  const currentUser = {
    alias: "",
    token: "",
    lastName: "",
    firstName: "",
    email: "",
    ldap: [] as string[],
    loc_descr: "",
  };

  // Listen for the customState event
  Hub.listen("auth", ({ payload: { data, event } }) => {
    switch (event) {
      case "customOAuthState":
        // set current url to federatedSignIn customState
        window.location.href = data;
        break;
    }
  });

  const user = await getCurrentUser();

  if (!user) {
    // TODO: add sign in log
    console.info("No session found, attempting to start a new one...");
    await signIn();
  }

  if (user) {
    user.getSession((_: Error | null, session: CognitoUserSession) => {
      if (session) {
        const token = session.getIdToken().getJwtToken();
        currentUser.token = token;
        const decodeToken = session.getIdToken().decodePayload();
        const emailClaim = decodeToken["email"];
        currentUser.alias =
          decodeToken?.identities?.[0]?.userId ??
          (typeof emailClaim === "string" ? emailClaim.split("@")[0] : "");
        const isTestUser =
          isBetaOrLocalEnv(stage) &&
          currentUser.alias?.startsWith(TEST_USER_PREFIX);
        if (isTestUser) {
          // Integration-test SAAM identity has no real LDAP membership; grant
          // the groups the smoke test needs so the app shell renders.
          currentUser.ldap = [...TEST_USER_LDAP_GROUPS];
        } else {
          const ldapClaim = decodeToken["custom:LDAP_GROUP"];
          currentUser.ldap = ldapClaim ? ldapClaim.split(",") : [];
        }
        currentUser.firstName = decodeToken["given_name"];
        currentUser.lastName = decodeToken["family_name"];
        currentUser.email = decodeToken["email"];
        // The SAAM integration-test identity's token has no custom:AMZN_LOC_DESCR
        // (nor email). Guard against it so the callback doesn't throw before the
        // token is persisted — an unguarded .split() here previously aborted
        // login mid-way, leaving localStorage["token"] unset and API calls 403.
        const locDescrClaim = decodeToken["custom:AMZN_LOC_DESCR"];
        currentUser.loc_descr =
          typeof locDescrClaim === "string"
            ? locDescrClaim.split("-")[0].trim()
            : "";
        localStorage.setItem("token", token);

        apiClient.interceptors.request.use(
          async (config) => {
            const authHeaders = await getAuthHeaders();
            config.headers = authHeaders;
            return config;
          },
          (error) => {
            Promise.reject(error);
          },
        );
      }
    });
  }
  return currentUser;
};

const getAssetsLastModified = async () => {
  const res = await axios.head(`https://gamma.latam.safer.whs.amazon.dev`);
  return res.headers["last-modified"];
};

const refreshPage = async () => {
  console.log("assets changed, refresh page...");
  window.location.reload();
};

const logout = () => {
  localStorage.removeItem(USER);
  localStorage.removeItem(TOKEN);
};

export default {
  login,
  logout,
};
